import { useEffect, useMemo, useState } from 'react';
import { Button, Page, Preloader } from 'konsta/react';
import { BadgeCheck, Lock, ShieldCheck, UserRoundCheck } from 'lucide-react';
import { MIN_PROVIDER_AGE, normalizeEthiopianPhone } from '@harmony/shared';
import { api, ApiError, type City, type Draft, type ProviderRegistration } from '../api';
import {
  BottomActions,
  Field,
  GenderPicker,
  LocationField,
  PhoneField,
  PhotoGrid,
  ProgressBar,
  SinglePhoto,
  TextInput,
  inputClass,
} from '../components/form';
import { haptic } from '../telegram';
import { useTelegramBack } from '../hooks';
import { useErrorText, useLang, useName, useT } from '../i18n';

const STEPS = ['basic', 'prices', 'photos', 'location', 'bio', 'id', 'agent', 'confirm'] as const;
type Step = (typeof STEPS)[number];

/** Server field → wizard step, to jump to the right place when the server rejects something. */
const FIELD_STEP: Record<string, Step> = {
  displayName: 'basic',
  gender: 'basic',
  phone: 'basic',
  age: 'basic',
  prices: 'prices',
  photoKeys: 'photos',
  location: 'location',
  bio: 'bio',
  idPhotoKey: 'id',
  selfieKey: 'id',
  agentCode: 'agent',
};

/**
 * Provider sign-up in small steps with a progress bar. Every "Next" saves a draft,
 * so the provider can close the app and continue later.
 */
export function ProviderWizard({
  initial,
  cities,
  agentHint,
  onSubmitted,
}: {
  initial: ProviderRegistration;
  cities: City[];
  agentHint: string | null;
  onSubmitted: (r: ProviderRegistration) => void;
}) {
  const t = useT();
  const lang = useLang();
  const name = useName();
  const errText = useErrorText();

  const [draft, setDraft] = useState<Draft>(() => ({ ...initial.draft }));
  const [photos, setPhotos] = useState(initial.previews.photos);
  const [idPhoto, setIdPhoto] = useState(initial.previews.idPhoto);
  const [selfie, setSelfie] = useState(initial.previews.selfie);
  const [agent, setAgent] = useState(initial.agent);
  const [agentInput, setAgentInput] = useState(initial.draft.agentCode ?? '');
  const [step, setStep] = useState<Step>('basic');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const idx = STEPS.indexOf(step);
  const goBack = idx > 0 ? () => setStep(STEPS[idx - 1]) : null;
  useTelegramBack(goBack);
  useEffect(() => setError(null), [step]);

  // Came from an agent's invite link: pre-fill that agent.
  useEffect(() => {
    if (initial.agentLocked || initial.draft.agentCode || !agentHint) return;
    api
      .agentLookup(agentHint)
      .then((a) => {
        setAgent(a);
        setAgentInput(a.agentCode);
        setDraft((d) => ({ ...d, agentCode: a.agentCode, agentVia: 'LINK' }));
      })
      .catch(() => undefined);
  }, []);

  const set = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }));

  /** Checks the current step before moving on. Returns an error message or null. */
  const check = (s: Step): string | null => {
    if (s === 'basic') {
      if (!draft.displayName || draft.displayName.trim().length < 2) return errText('NAME_TOO_SHORT');
      if (!draft.gender) return `${t('reg.gender')}: ${errText('REQUIRED')}`;
      if (!draft.phone || !normalizeEthiopianPhone(draft.phone)) return errText('INVALID_PHONE');
      if (!draft.age || draft.age < MIN_PROVIDER_AGE) return errText('UNDER_18');
    }
    if (s === 'prices' && !Object.keys(draft.prices ?? {}).length) return errText('PRICE_REQUIRED');
    if (s === 'photos' && !photos.length) return errText('PHOTO_REQUIRED');
    if (s === 'location' && !draft.location?.cityId && draft.location?.lat === undefined) return errText('LOCATION_REQUIRED');
    if (s === 'id' && !idPhoto) return errText('ID_PHOTO_REQUIRED');
    if (s === 'id' && !selfie) return errText('SELFIE_REQUIRED');
    // A typed code must be checked before moving on, so it is never silently dropped.
    if (s === 'agent' && !initial.agentLocked && agentInput.trim() && !agent) return t('reg.agent_question');
    return null;
  };

  const payload = (d: Draft = draft): Partial<Draft> => ({
    ...d,
    phone: d.phone ? normalizeEthiopianPhone(d.phone) ?? d.phone : undefined,
    photoKeys: photos.map((p) => p.key),
  });

  /** Saves the draft (with any last-moment change) and moves to the next step. */
  const next = async (patch: Partial<Draft> = {}) => {
    const problem = check(step);
    if (problem) {
      haptic('error');
      return setError(problem);
    }
    const merged = { ...draft, ...patch };
    setDraft(merged);
    setBusy(true);
    try {
      await api.saveDraft(payload(merged));
      setStep(STEPS[idx + 1]);
    } catch (e) {
      haptic('error');
      setError(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.saveDraft(payload());
      const r = await api.submitProvider();
      haptic('success');
      onSubmitted(r);
    } catch (e) {
      haptic('error');
      const field = e instanceof ApiError ? e.issues[0]?.path?.split('.')[0] : undefined;
      if (field && FIELD_STEP[field]) setStep(FIELD_STEP[field]);
      setTimeout(() => setError(errText(e)), 0);
    } finally {
      setBusy(false);
    }
  };

  const titles: Record<Step, string> = {
    basic: t('reg.step_basic'),
    prices: t('reg.step_prices'),
    photos: t('reg.step_photos'),
    location: t('reg.step_location'),
    bio: t('reg.step_bio'),
    id: t('reg.step_id'),
    agent: t('reg.step_agent'),
    confirm: t('reg.step_confirm'),
  };

  const cityLabel = useMemo(() => {
    if (draft.location?.lat !== undefined) return t('reg.location_saved');
    return name(cities.find((c) => c.id === draft.location?.cityId));
  }, [draft.location, cities, lang]);

  return (
    <Page className="flex flex-col">
      <ProgressBar step={idx + 1} total={STEPS.length} />
      <div className="flex-1 px-5 pt-5">
        <h1 className="mb-5 text-2xl font-semibold">{titles[step]}</h1>

        {step === 'basic' && (
          <>
            <Field label={t('reg.name')} hint={t('reg.name_hint')}>
              <TextInput value={draft.displayName ?? ''} maxLength={40} onChange={(e) => set({ displayName: e.target.value })} />
            </Field>
            <Field label={t('reg.gender')}>
              <GenderPicker value={draft.gender} onChange={(gender) => set({ gender })} />
            </Field>
            <Field label={t('reg.phone')}>
              <PhoneField value={draft.phone ?? ''} onChange={(phone) => set({ phone })} />
            </Field>
            <Field label={t('reg.age')} hint={t('reg.age_hint')}>
              <TextInput
                type="number"
                inputMode="numeric"
                min={MIN_PROVIDER_AGE}
                max={80}
                value={draft.age ?? ''}
                onChange={(e) => set({ age: e.target.value ? Number(e.target.value) : undefined })}
              />
            </Field>
          </>
        )}

        {step === 'prices' && (
          <>
            <p className="mb-5 text-tg-hint">{t('reg.price_hint')}</p>
            {initial.sessionTypes.map((s) => (
              <Field key={s.id} label={t('reg.price_for', { minutes: s.minutes })}>
                <div className="relative">
                  <TextInput
                    type="number"
                    inputMode="numeric"
                    min={50}
                    placeholder="0"
                    value={draft.prices?.[String(s.minutes)] ?? ''}
                    onChange={(e) => {
                      const prices = { ...(draft.prices ?? {}) };
                      if (e.target.value) prices[String(s.minutes)] = Number(e.target.value);
                      else delete prices[String(s.minutes)];
                      set({ prices });
                    }}
                    className="pr-16"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-tg-hint">ETB</span>
                </div>
              </Field>
            ))}
          </>
        )}

        {step === 'photos' && (
          <>
            <p className="mb-4 text-tg-hint">{t('reg.photos_hint')}</p>
            <PhotoGrid photos={photos} onChange={setPhotos} />
          </>
        )}

        {step === 'location' && (
          <LocationField cities={cities} value={draft.location ?? {}} onChange={(location) => set({ location })} />
        )}

        {step === 'bio' && (
          <Field label={t('reg.bio')} hint={`${t('reg.bio_hint')} ${(draft.bio ?? '').length}/70`}>
            <textarea
              className={inputClass + ' min-h-[96px] resize-none'}
              maxLength={70}
              value={draft.bio ?? ''}
              onChange={(e) => set({ bio: e.target.value })}
            />
          </Field>
        )}

        {step === 'id' && (
          <>
            <div className="mb-5 flex gap-3 rounded-2xl bg-tg-secondary p-4 text-sm">
              <ShieldCheck className="shrink-0 text-green-600" size={22} />
              <span>{t('reg.id_private')}</span>
            </div>
            <SinglePhoto
              purpose="id_photo"
              url={idPhoto}
              label={t('reg.id_photo')}
              hint={t('reg.id_hint')}
              onChange={(r) => {
                setIdPhoto(r.url);
                set({ idPhotoKey: r.key });
              }}
            />
            <SinglePhoto
              purpose="id_selfie"
              selfie
              url={selfie}
              label={t('reg.selfie')}
              hint={t('reg.selfie_hint')}
              onChange={(r) => {
                setSelfie(r.url);
                set({ selfieKey: r.key });
              }}
            />
          </>
        )}

        {step === 'agent' &&
          (initial.agentLocked && initial.agent ? (
            <div className="flex items-center gap-3 rounded-2xl bg-tg-secondary p-4">
              <Lock size={20} className="text-tg-hint" />
              <span className="font-medium">{t('reg.agent_locked', { name: initial.agent.name })}</span>
            </div>
          ) : (
            <>
              {draft.agentVia === 'LINK' && agent && <p className="mb-4 text-tg-hint">{t('reg.agent_from_link', { name: agent.name })}</p>}
              <Field label={t('reg.agent_question')}>
                <div className="flex gap-2">
                  <TextInput
                    value={agentInput}
                    placeholder={t('reg.agent_placeholder')}
                    autoCapitalize="characters"
                    onChange={(e) => {
                      setAgentInput(e.target.value.toUpperCase());
                      setAgent(null);
                      set({ agentCode: undefined, agentVia: undefined });
                    }}
                  />
                  <Button
                    rounded
                    className="!w-auto shrink-0 px-5"
                    disabled={!agentInput.trim()}
                    onClick={async () => {
                      try {
                        const a = await api.agentLookup(agentInput.trim());
                        haptic('success');
                        setAgent(a);
                        setError(null);
                        set({ agentCode: a.agentCode, agentVia: draft.agentVia === 'LINK' ? 'LINK' : 'CODE' });
                      } catch (e) {
                        setError(errText(e));
                      }
                    }}
                  >
                    {t('reg.agent_check')}
                  </Button>
                </div>
              </Field>
              {agent && (
                <div className="flex items-center gap-3 rounded-2xl bg-green-500/10 p-4 text-green-700">
                  <UserRoundCheck size={22} />
                  <span className="font-medium">{t('reg.agent_found', { name: agent.name })}</span>
                </div>
              )}
            </>
          ))}

        {step === 'confirm' && (
          <div className="space-y-4">
            {photos.length > 0 && (
              <div className="grid grid-cols-3 gap-2">
                {photos.map((p) => (
                  <img key={p.key} src={p.url} className="aspect-square w-full rounded-xl object-cover" alt="" />
                ))}
              </div>
            )}
            <div className="overflow-hidden rounded-2xl bg-tg-secondary">
              {[
                [t('reg.name'), draft.displayName],
                [t('reg.gender'), draft.gender ? t(`gender.${draft.gender}`) : ''],
                [t('reg.phone'), draft.phone ? normalizeEthiopianPhone(draft.phone) : ''],
                [t('reg.age'), draft.age],
                ...initial.sessionTypes
                  .filter((s) => draft.prices?.[String(s.minutes)])
                  .map((s) => [t('reg.price_for', { minutes: s.minutes }), `${draft.prices![String(s.minutes)]} ETB`]),
                [t('reg.location'), cityLabel],
                [t('reg.bio'), draft.bio || '—'],
                [t('reg.id_docs'), idPhoto && selfie ? t('reg.provided') : t('reg.missing')],
                [t('reg.step_agent'), agent?.name ?? t('reg.agent_none')],
              ].map(([k, v]) => (
                <div key={String(k)} className="flex justify-between gap-4 border-b border-tg-secondary bg-tg-bg px-4 py-3 last:border-0">
                  <span className="text-tg-hint">{k}</span>
                  <span className="text-right font-medium">{v}</span>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              {STEPS.filter((s) => s !== 'confirm').map((s) => (
                <button key={s} onClick={() => setStep(s)} className="rounded-xl bg-tg-secondary py-2 text-tg-link">
                  {t('reg.edit')}: {titles[s]}
                </button>
              ))}
            </div>
          </div>
        )}

        {error && <p className="mt-4 rounded-xl bg-red-500/10 p-3 text-red-600">{error}</p>}
      </div>

      <BottomActions>
        {step === 'confirm' ? (
          <Button large rounded disabled={busy} onClick={submit}>
            {busy ? <Preloader className="h-5 w-5" /> : t('reg.submit')}
          </Button>
        ) : (
          <Button large rounded disabled={busy} onClick={() => next()}>
            {busy ? <Preloader className="h-5 w-5" /> : t('reg.next')}
          </Button>
        )}
        {(step === 'bio' || step === 'agent') && (
          <Button
            large
            rounded
            clear
            onClick={() => {
              if (step === 'bio') return next({ bio: '' });
              if (initial.agentLocked) return next();
              setAgent(null);
              setAgentInput('');
              return next({ agentCode: '', agentVia: undefined });
            }}
          >
            {t('reg.skip')}
          </Button>
        )}
      </BottomActions>
    </Page>
  );
}

export function PendingScreen() {
  const t = useT();
  return (
    <Page className="flex flex-col items-center justify-center px-8 text-center">
      <BadgeCheck size={72} className="mb-4 text-tg-button" />
      <h1 className="mb-3 text-2xl font-semibold">{t('status.pending_title')}</h1>
      <p className="text-tg-hint">{t('status.pending_body')}</p>
    </Page>
  );
}

export function DeniedScreen({ reason, onReapply }: { reason: string | null; onReapply: () => void }) {
  const t = useT();
  return (
    <Page className="flex flex-col">
      <div className="flex flex-1 flex-col items-center justify-center px-8 text-center">
        <div className="mb-4 text-6xl">📝</div>
        <h1 className="mb-3 text-2xl font-semibold">{t('status.denied_title')}</h1>
        {reason && <p className="rounded-2xl bg-tg-secondary p-4">{t('status.denied_reason', { reason })}</p>}
      </div>
      <BottomActions>
        <Button large rounded onClick={onReapply}>
          {t('status.reapply')}
        </Button>
      </BottomActions>
    </Page>
  );
}
